import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { podcastsService } from '../services/podcasts.service';
import type { SavePodcastPayload } from '../types/api.types';
import toast from 'react-hot-toast';

export const usePodcastSearch = (searchTerm: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['podcast', 'search', searchTerm],
    queryFn: () => podcastsService.searchPodcast({ podcastName: searchTerm }),
    enabled: enabled && searchTerm.length > 0,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

// New infinite query hook for paginated search results
export const usePodcastSearchInfinite = (searchTerm: string, enabled: boolean = false) => {
  return useInfiniteQuery({
    queryKey: ['podcast', 'search-infinite', searchTerm],
    queryFn: ({ pageParam = 1 }) => 
      podcastsService.searchPodcast({ 
        podcastName: searchTerm, 
        page: pageParam,
        limitPerPage: 20 // Podcasts per page
      }),
    getNextPageParam: (lastPage, allPages) => {
      const currentCount = allPages.reduce((sum, page) => sum + page.results.length, 0);
      const totalCount = lastPage.totalCount;
      
      // If we have more podcasts to fetch, return the next page number
      if (currentCount < totalCount) {
        return allPages.length + 1;
      }
      
      // No more pages
      return undefined;
    },
    enabled: enabled && searchTerm.length > 0,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    initialPageParam: 1,
  });
};

export const usePodcastEpisodes = (podcastName: string, enabled: boolean = false) => {
  return useQuery({
    queryKey: ['podcast', 'episodes', podcastName],
    queryFn: () => podcastsService.getPodcastEpisodes({ podcastName }),
    enabled: enabled && podcastName.length > 0,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

// New infinite query hook for paginated episodes
export const usePodcastEpisodesInfinite = (podcastName: string, enabled: boolean = false) => {
  return useInfiniteQuery({
    queryKey: ['podcast', 'episodes-infinite', podcastName],
    queryFn: ({ pageParam = 1 }) => 
      podcastsService.getPodcastEpisodes({ 
        podcastName, 
        page: pageParam,
        limitPerPage: 25 // Episodes per page
      }),
    getNextPageParam: (lastPage, allPages) => {
      const currentEpisodeCount = allPages.reduce((sum, page) => sum + (page.podcastEpisodes?.length || 0), 0);
      const totalCount = lastPage.totalEpisodesCount || 0;
      
      // If we have more episodes to fetch, return the next page number
      if (currentEpisodeCount < totalCount) {
        return allPages.length + 1;
      }
      
      // No more pages
      return undefined;
    },
    enabled: enabled && podcastName.length > 0,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    initialPageParam: 1,
  });
};

export const useAddEpisodeToProject = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      projectId,
      payload,
    }: {
      projectId: number;
      payload: SavePodcastPayload;
    }) => podcastsService.addEpisodeToProject(projectId, payload),
    onSuccess: (_data, variables) => {
      // Invalidate project-specific queries to refetch podcasts for this project
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'podcasts'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
      // Also invalidate the main projects list to update any episode counts
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Episode added to project successfully!');
    },
    onError: (error: any) => {      console.error('Delete episode error:', error);      toast.error(error?.response?.data?.message || 'Failed to add episode to project');
    },
  });
};

export const useUploadAudioFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      formData,
    }: {
      projectId: number;
      formData: FormData;
    }) => podcastsService.uploadAudioFile(projectId, formData),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'podcasts'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Episode added to project successfully!');
    },
    onError: (error: any) => {
      console.error('Upload audio error:', error);
      toast.error(error?.response?.data?.message || 'Failed to upload audio file');
    },
  });
};

export const useUpdateEpisodeAccess = () => {
  return useMutation({
    mutationFn: (episodeId: number) => podcastsService.updateEpisodeAccess(episodeId),
    // Silent update, no toast notifications
  });
};

export const useProjectPodcasts = (projectId: number) => {
  return useQuery({
    queryKey: ['project', projectId, 'podcasts'],
    queryFn: () => podcastsService.getPodcastsByProject(projectId),
    enabled: !!projectId,
  });
};

export const useDeleteEpisode = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      projectId,
      episodeId,
    }: {
      projectId: number;
      episodeId: number;
    }) => {
      console.log('usePodcasts: deleteEpisode mutation called with projectId:', projectId, 'episodeId:', episodeId);
      return podcastsService.deleteEpisode(projectId, episodeId);
    },
    onSuccess: (_data, variables) => {
      console.log('usePodcasts: deleteEpisode onSuccess, invalidating queries');
      // Invalidate the project query to refetch with updated episodes
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
      // Invalidate the projects list so ProjectList shows updated episode counts
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Also invalidate the project-podcasts query used by ProjectDetail
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId, 'podcasts'] });
      toast.success('Episode removed from project');
    },
    onError: (error: any) => {
      console.error('usePodcasts: deleteEpisode onError:', error);
      toast.error(error?.response?.data?.message || 'Failed to remove episode');
    },
  });
};
